import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { MEDIA } from "@/constants/media";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { t } = useSite();
  const h = t.hero;

  return (
    <section id="hero" data-testid="hero-section" className="relative grain min-h-screen overflow-hidden">
      {/* Right full-bleed silk image */}
      <div className="absolute inset-y-0 right-0 hidden w-[46%] lg:block">
        <motion.img
          src={MEDIA.heroSilk}
          alt="Premium draped silk fabric"
          className="h-full w-full object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/40 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-6 pt-28 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex items-center gap-3"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="font-body text-xs font-medium uppercase tracking-luxe text-gold">
            {h.overline}
          </span>
        </motion.div>

        <h1 className="mt-8 max-w-3xl font-heading text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl md:text-7xl lg:text-8xl">
          <motion.span
            className="block"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          >
            {h.titleTop}
          </motion.span>
          <motion.span
            className="block italic text-gold"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
          >
            {h.titleBottom}
          </motion.span>
        </h1>

        <motion.p
          className="mt-8 max-w-xl font-body text-base font-light leading-relaxed text-muted-foreground sm:text-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45, ease: EASE }}
        >
          {h.subtitle}
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("contact")}
            className="group flex items-center gap-3 bg-gold px-8 py-4 font-body text-sm font-medium uppercase tracking-[0.12em] text-black transition-opacity hover:opacity-85"
          >
            {h.cta}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("services")}
            className="border border-border px-8 py-4 font-body text-sm font-medium uppercase tracking-[0.12em] text-foreground transition-colors hover:border-gold hover:text-gold"
          >
            {h.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          className="mt-16 grid max-w-2xl grid-cols-3 gap-8 border-t border-border pt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
        >
          {[
            [h.stat1v, h.stat1],
            [h.stat2v, h.stat2],
            [h.stat3v, h.stat3],
          ].map(([v, label], i) => (
            <div key={i}>
              <div className="font-heading text-3xl font-light text-gold sm:text-4xl">{v}</div>
              <div className="mt-1 font-body text-xs uppercase tracking-[0.12em] text-muted-foreground">
                {label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
